import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { Router, ActivatedRoute } from '@angular/router';
import { LikutBarcodeDefinition, LikutProductBarcode, LikutBarcodeDefinitionPayload } from '@interfaces/likut';
import { ResponseFromExpress, SQLError, SQLResponse } from '@interfaces/SQL-interfaces';
import { AllProductsLikut } from '@interfaces/SQL-morning-responses';
import { BarcodeService } from '@services/barcode.service';
import { BottomPopupService } from '@services/bottom-popup.service';
import { LikutScanService } from '@services/likut-scan.service';
import { LoadingService } from '@services/loading.service';
import { ProductsService } from '@services/products.service';
import { StoreService } from '@services/store.service';
import { TextService } from '@services/text.service';
import { MAXIMUM_PACK_QUANTITY } from '@utils/constants-products';
import { decompress } from 'lz-string';
import { BehaviorSubject, Subject, map, takeUntil } from 'rxjs';

type BarcodeDefinitionDisplay = LikutBarcodeDefinition & { hasTokef: boolean; hasQuantity: boolean; hasPackQuantity: boolean };

const formDefaultValue = {
	BarcodeName: '',
	BarcodeLen: null,
	MakatLoc: null,
	MakatLen: null,
	QtyLoc: null,
	QtyLen: null,
	QtyDot: null,
	TokefDay: null,
	TokefMonth: null,
	TokefYear: null,
	YearSegment: null,
	PackIdLen: null,
	PackIdLoc: null,
	PackQTY: null,
	PackageSug: null,
};
@Component({
	selector: 'app-likut-barcode-definitions-management',
	templateUrl: './likut-barcode-definitions-management.component.html',
	styleUrls: ['./likut-barcode-definitions-management.component.scss'],
})
export class LikutBarcodeDefinitionsManagementComponent implements OnInit, OnDestroy {
	_text = inject(TextService);
	private _ss = inject(StoreService);
	private router = inject(Router);
	private fb = inject(FormBuilder);
	private _products = inject(ProductsService);
	private _loading = inject(LoadingService);
	private _bottomPopup = inject(BottomPopupService);
	private _likutScan = inject(LikutScanService);
	private _barcode = inject(BarcodeService);

	constructor() {}
	form = this.fb.nonNullable.group({
		// TODO add custom validator - barcodelen can't be shorter than sum of length of all other fields
		//? Maybe group common fields, and have a custom validator for the group
		BarcodeName: ['', Validators.required],
		BarcodeLen: [<number>null, [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1)]],
		MakatLoc: [<number>null, [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1)]],
		MakatLen: [<number>null, [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1)]],
		QtyLoc: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		QtyLen: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		QtyDot: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1), Validators.max(5)]],
		TokefDay: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		TokefMonth: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		TokefYear: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		YearSegment: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(2), Validators.max(4)]],
		PackIdLoc: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		PackIdLen: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		PackQTY: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
		PackageSug: [<number>null, [Validators.pattern(/^\d+$/), Validators.min(1)]],
	});

	products$ = new BehaviorSubject<LikutProductBarcode[]>([]);
	definitions$ = new BehaviorSubject<LikutBarcodeDefinition[]>([]);
	definitionsDisplay$ = this.definitions$.pipe(
		map(definitions =>
			definitions.map(definition => ({
				...definition,
				hasTokef: Boolean(definition.TokefDay && definition.TokefMonth && definition.TokefYear && definition.YearSegment),
				hasQuantity: Boolean(definition.QtyLoc && definition.QtyLen && definition.QtyDot),
				hasPackQuantity: Boolean(definition.PackIdLen && definition.PackIdLoc && definition.PackQTY && definition.PackageSug),
			})),
		),
	);
	productsFromMemory$ = new BehaviorSubject<AllProductsLikut[]>([]);
	productsFromMemoryDictionary$ = this.productsFromMemory$.pipe(map(products => products.reduce((acc, curr) => ({ ...acc, [curr.ProductId]: curr }), {})));

	loading = false;

	updateSuccess = false;

	destroy$: Subject<void> = new Subject();

	isEdittingBarcodeDefinition = false;
	selectedBarcodeDefinitionID = null;

	isLoadingProgressBarShowing = false;

	productBarcodeDrawerOpen = false;

	ngOnInit() {
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////
		if (this._ss.CustomerInformation === null) {
			const CustomerInformation = localStorage.getItem('CustomerInformation');
			if (!CustomerInformation) {
				this.router.navigate(['login']);
				return;
			}

			this._ss.CustomerInformation = JSON.parse(decompress(CustomerInformation));
		}
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////

		this._ss.CompanyNumForDuplicate = null;

		window.scrollTo(0, 0);

		this._ss.Betipul = 0;
		this._ss.TeudotDetailsEach = [];
		if (localStorage.getItem('UserName') !== null && localStorage.getItem('AgentId') !== null) {
			this._ss.UserNameConnected = localStorage.getItem('UserName');
			this._ss.AgentIdConnected = localStorage.getItem('AgentId');
		} else {
			this.router.navigate(['login']);
		}

		this.initiateListeners();

		this.getInitialData();
	}

	async getInitialData() {
		this._loading.changeLoadingStatuses({ isLoading: true });
		try {
			const products = await this._products.getProductsWithoutAgent('likut');
			if (!products) {
				throw new Error('No products found');
			}
			this.productsFromMemory$.next(products);
		} catch (error) {
			alert('לא ניתן לטעון פריטים, אנא נסה שנית מאוחר יותר');
			this.router.navigate(['home']);
		}

		await this.handleRefreshBarcodes();
		this._loading.changeLoadingStatuses({ isLoading: false });
	}

	async handleRefreshBarcodes() {
		if (!this._likutScan.barcodeDefinitions$.value?.length || !this._likutScan.barcodeToProducts$.value?.length) {
			await this._likutScan.handleGetBarcodeDefinitions(this.destroy$);
			await this._likutScan.handleGetBarcodeToProducts(this.destroy$);
		}

		this.products$.next(this._likutScan.barcodeToProducts$.value);
		this.definitions$.next(this._likutScan.barcodeDefinitions$.value);
	}

	closeDrawer() {
		this.isEdittingBarcodeDefinition = false;
		this.form.reset();
		this.updateSuccess = false;
		this.form.enable();
		this.showHideFilterPopup(false);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	showHideFilterPopup(value): void {
		this.productBarcodeDrawerOpen = value;
	}

	async handleFormSubmit() {
		this.form.markAllAsTouched();
		if (this.form.invalid) return;

		const { BarcodeLen, BarcodeName, MakatLoc, MakatLen, QtyLoc, QtyLen, QtyDot, TokefDay, TokefMonth, TokefYear, YearSegment, PackIdLen, PackIdLoc, PackQTY, PackageSug } = this.form.value;

		const body: LikutBarcodeDefinitionPayload = {
			BarcodeName,
			BarcodeLen,
			MakatLen,
			MakatLoc,
			QtyLen,
			QtyLoc,
			QtyDot,
			TokefDay,
			TokefMonth,
			TokefYear,
			YearSegment,
			PackIdLen,
			PackIdLoc,
			PackQTY,
			PackageSug,
		};

		if (this.isEdittingBarcodeDefinition) {
			await this.handlePatchDefinition(this.selectedBarcodeDefinitionID, body);
			return;
		}

		try {
			await this.handlePostDefinition(body);
		} catch (error) {}
	}

	handlePostDefinition(body: LikutBarcodeDefinitionPayload) {
		return new Promise<void>((resolve, reject) => {
			this._likutScan.postBarcodeDefinition(body).subscribe({
				next: res => {
					this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this.updateSuccess = false;
					this._loading.changeLoadingStatuses({ isLoading: false });
					reject();
				},
			});
		});
	}

	async handlePatchDefinition(rowID: number, postBody: LikutBarcodeDefinitionPayload) {
		return new Promise<void>((resolve, reject) => {
			this._likutScan.patchBarcodeDefinition(rowID, postBody).subscribe({
				next: res => {
					this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this.updateSuccess = false;
					this._loading.changeLoadingStatuses({ isLoading: false });
					reject();
				},
			});
		});
	}

	handleDeleteDefinition(rowID: number) {
		return new Promise<void>((resolve, reject) => {
			this._likutScan.deleteBarcodeDefinition(rowID).subscribe({
				next: async res => {
					await this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this._loading.changeLoadingStatuses({ isLoading: false });
					this.updateSuccess = false;
					reject();
				},
			});
		});
	}

	openNewBarcodeProductDrawer(formData: Required<typeof this.form.value> = { ...formDefaultValue }) {
		this.showHideFilterPopup(true);

		this.form.setValue(formData);
		setTimeout(() => {
			this.form.updateValueAndValidity();
		}, 0);
	}

	handleEditProductBarcode(product: BarcodeDefinitionDisplay) {
		this.isEdittingBarcodeDefinition = true;
		const values = structuredClone(formDefaultValue);
		const barcodeProduct = structuredClone(product);

		this.selectedBarcodeDefinitionID = barcodeProduct.Id;

		const payload = {
			...values,
			...barcodeProduct,
		};

		delete payload.Id;
		delete payload.hasTokef;
		delete payload.hasQuantity;
		delete payload.hasPackQuantity;

		this.openNewBarcodeProductDrawer(payload);
	}
	// SQLResponse<LikutBarcodeDefinition>[] | SQLError
	async handleAfterHttpRequest(res: ResponseFromExpress<LikutBarcodeDefinition>, resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void) {
		this._loading.changeLoadingStatuses({ isLoading: false });

		if (res === 'ConnectionError' || res === 'RequestError') {
			console.log(res);
			alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
			return reject();
		}

		this.updateSuccess = true;

		this.selectedBarcodeDefinitionID = null;
		this.isEdittingBarcodeDefinition = false;

		this.definitions$.next(res.recordset);
		this._likutScan.updateStorage({ barcodeDefinitions: res.recordset });
		resolve();
	}

	async handleChangeBarcodeDefinition(e: MatButtonToggleChange | MouseEvent | PointerEvent | Event, rowID: number, action: 'delete') {
		if (this.isLoadingProgressBarShowing) return;

		this.isLoadingProgressBarShowing = true;

		let msg = 'קרתה תקלה בתקשורת נתונים, אנא נסו שנית';

		if (action === 'delete' && !(e instanceof MatButtonToggleChange)) {
			try {
				await this.handleDeleteDefinition(rowID);
				msg = 'הגדרה נמחקה בהצלחה';
				this.updateSuccess = false;
			} catch (error) {}
		}

		this._bottomPopup.show(msg, { action: '' });

		this.isLoadingProgressBarShowing = false;
	}

	initiateListeners() {
		this._barcode
			.createBarcodeStream()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: barcode => {
					this.form.controls.BarcodeLen.setValue(barcode.length);
				},
			});
	}

	BackToDohot() {
		history.back();
	}
}
